import { HouseIcon, Typography } from '@/components/atoms';
import {
  CalendarIcon,
  MapPinnedIcon,
} from '@/components/atoms/icons';
import { FullScreenLoader, OkDialog } from '@/components/molecules';
import { MessageOkDialog } from '@/components/molecules/dialogs/MessageOkDialog';
import { ImageCarousel } from '@/components/molecules/ImageCarousel';
import WebAppLayout from '@/components/organisms/WebAppLayout';
import { useWebDocumentTitle } from '@/core/hooks/useWebDocumentTitle';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN, buildUnitDisplayName } from '@/core/constants/dynatraceCatalog';
import { WEB_PAGE_TOP_BELOW_HEADER } from '@/core/constants/webLayout';
import { useCatalogDynatraceView } from '@/core/hooks/useCatalogDynatraceView';
import { useTranslation, useUIColors, useWebViewport } from '@/core/hooks';
import {
  adhaUnitDetailProperties,
  trackCatalogEvent,
} from '@/core/services/trackDynatraceCatalog';
import { logger } from '@/core/logger';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { hasTextValue } from '@/core/utils/fieldAvailability';
import {
  getReservationCtas,
  ReservationCta,
  type LocalReservationSignatureStatus,
  type ReservationStatus,
} from '@/core/reservation';
import { shareFile } from '@/core/wrappers/sharing';
import {
  AppointmentFlowDialogs,
  AppointmentStatusCard,
  useBookAppointmentFlow,
} from '@/modules/book-appointment';
import { useIsFocused } from '@react-navigation/native';
import { router } from 'expo-router';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { UnitAboutAccordion } from '../components/UnitAboutAccordion';
import { UnitActionCard } from '../components/UnitActionCard';
import { ConfirmReservationDialog } from '../components/ConfirmReservationDialog';
import { ReservationStatusCard } from '../components/ReservationStatusCard';
import { CancelReservationDialog } from '../components/CancelReservationDialog';
import { ReservationCanceledDialog } from '../components/ReservationCanceledDialog';
import { ReservationSummarySheet } from '../components/ReservationSummarySheet';
import { UnitDocumentsAccordion } from '../components/UnitDocumentsAccordion';
import { UnitNextStepSheet } from '../components/UnitNextStepSheet';
import { UnitPriceSummary } from '../components/UnitPriceSummary';
import { CompletePaymentDialog } from '../components/CompletePaymentDialog';
import { BankTransferDetailsDialog } from '../components/BankTransferDetailsDialog';
import { SubmissionReceivedDialog } from '../components/SubmissionReceivedDialog';
import { UnitReservedStickyBanner } from '../components/UnitReservedStickyBanner';
import { UnitStickyFooter } from '../components/UnitStickyFooter';
import { UnitReservedSuccessDialog } from '../components/UnitReservedSuccessDialog';
import type { UnitDetailsLabels } from '../constants/getPropertyLabels';
import { usePropertyLabels } from '../hooks/usePropertyLabels';
import {
  PropertiesDetailsScreenSkeleton,
  UnitActionCardSkeleton,
} from '../components/skeleton';
import { usePropertiesDetails } from '../hooks/usePropertiesDetails';
import { useCompletePaymentFlow } from '../hooks/useCompletePaymentFlow';
import { PropertiesDetailsLoadingShell } from './PropertiesDetailsLoadingShell';
import { getUnitDetailsLayout } from './unitDetailsLayout';

/**
 * Web unit details screen (property catalogue).
 * Desktop (≥ 1100): gallery + info left, price/actions sidebar right.
 * Phone + tablet: stacked layout with sticky bottom bar.
 *
 * First paint is the shimmer shell; heavy hooks/UI mount on the next tick
 * so list → details navigation can paint immediately.
 */
export function PropertiesDetailsScreen() {
  const [contentReady, setContentReady] = useState(false);

  useEffect(() => {
    // Defer only one tick — do not use startTransition (can stall under concurrent nav).
    const timerId = setTimeout(() => {
      setContentReady(true);
    }, 0);
    return () => {
      clearTimeout(timerId);
    };
  }, []);

  if (!contentReady) {
    return <PropertiesDetailsLoadingShell />;
  }

  return <PropertiesDetailsScreenContent />;
}

function PropertiesDetailsScreenContent() {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const { breakpointWidth: width } = useWebViewport();
  const { isPhone, isTablet, isCompact, carouselHeight } =
    getUnitDetailsLayout(width);
  const { UNIT_DETAILS: labels } = usePropertyLabels();

  const [nextStepVisible, setNextStepVisible] = useState(false);

  const {
    unit,
    unitNotFound,
    isLoading,
    unitImages,
    unitImagesLoading,
    handleMapPress,
    handleReserveUnit,
    isReserveDisabled,
    activeReservationErrorVisible,
    handleCloseActiveReservationError,
    documentErrorMessage,
    handleCloseDocumentError,
    handleViewReservation,
    handleCloseReservationSummary,
    reservationSummaryVisible,
    handleCloseConfirmReservation,
    handleProceedReservation,
    confirmReservationVisible,
    handleCloseUnitReservedSuccess,
    unitReservedSuccessVisible,
    isUnitReserved,
    reservationSignatureStatus,
    reservationMappedStatus,
    reservedDateDisplay,
    requestedOnDisplay,
    cancelReservationVisible,
    reservationCanceledVisible,
    handleCloseCancelReservation,
    handleConfirmCancelReservation,
    handleCloseReservationCanceled,
    isFloorPlanAvailable,
    handleViewFloorPlan,
    floorPlanLoading,
    floorPlanSuccessMessage,
    floorPlanSavedUri,
    floorPlanErrorMessage,
    handleCloseFloorPlanSuccess,
    handleCloseFloorPlanError,
    handleDocumentPress,
    downloadingDocumentId,
    reservationRequestPending,
    bookingDetailSyncPending,
    isAppointmentBookingEnabled,
    profileIskanLoanAmount,
  } = usePropertiesDetails();

  const unitId = unit?.id ?? '';
  const unitDetailProps = unitId
    ? adhaUnitDetailProperties({
        unitId,
        unitName: buildUnitDisplayName({
          projectName: unit?.projectName,
          unitNumber: unit?.unitNumber,
          unitType: unit?.unitType,
          buildingName: unit?.buildingName,
        }),
        projectId: unit?.projectId,
        projectName: unit?.projectName,
      })
    : undefined;

  useCatalogDynatraceView(
    ALMETLAA_SCREEN.UNIT,
    unitId ? ALMETLAA_ACTION.UNIT.detailPage(unitId) : '',
    Boolean(unit?.id),
    unitDetailProps,
  );

  const trackUnitAction = useCallback(
    (actionName: string) => {
      if (!unitId) {
        return;
      }
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.UNIT,
        actionName,
        eventType: 'action',
        properties: unitDetailProps,
      });
    },
    [unitDetailProps, unitId],
  );

  const onReserveUnitTracked = useCallback(() => {
    trackUnitAction(ALMETLAA_ACTION.UNIT.reserveClick(unitId));
    handleReserveUnit();
  }, [handleReserveUnit, trackUnitAction, unitId]);

  const onDocumentPressTracked = useCallback(
    (documentId: string) => {
      trackUnitAction(
        ALMETLAA_ACTION.UNIT.BROCHURE_DOWNLOAD_TEMPLATE.replace('{unitId}', unitId),
      );
      handleDocumentPress(documentId);
    },
    [handleDocumentPress, trackUnitAction, unitId],
  );

  const appointmentFlow = useBookAppointmentFlow(
    unit?.id ? { unitId: unit.id } : { enableUpcoming: false },
  );
  const { resetDialogs } = appointmentFlow;
  const isFocused = useIsFocused();

  // Avoid stacking Modals if this screen stays mounted under another route.
  useEffect(() => {
    if (!isFocused) {
      resetDialogs();
      handleCloseConfirmReservation();
      handleCloseUnitReservedSuccess();
      handleCloseCancelReservation();
      handleCloseReservationCanceled();
      handleCloseReservationSummary();
    }
  }, [
    isFocused,
    resetDialogs,
    handleCloseConfirmReservation,
    handleCloseUnitReservedSuccess,
    handleCloseCancelReservation,
    handleCloseReservationCanceled,
    handleCloseReservationSummary,
  ]);

  const openBookAppointment = useCallback(() => {
    appointmentFlow.openBook();
  }, [appointmentFlow]);

  const onGalleryOpen = useCallback(() => {
    if (!unitId) {
      return;
    }
    trackUnitAction(
      ALMETLAA_ACTION.UNIT.GALLERY_OPEN_TEMPLATE.replace('{unitId}', unitId),
    );
  }, [trackUnitAction, unitId]);

  const onUnitShare = useCallback(() => {
    if (!unitId) {
      return;
    }
    trackUnitAction(
      ALMETLAA_ACTION.UNIT.SHARE_CLICK_TEMPLATE.replace('{unitId}', unitId),
    );
  }, [trackUnitAction, unitId]);

  const homePath = '/(auth)/(home)';
  const pageTitle = labels.TITLE;

  const layoutProps = {
    activeTab: 'home' as const,
    headerTitle: pageTitle,
    breadcrumbs: [
      { label: labels.HOME_BREADCRUMB, path: homePath, replace: true },
      { label: pageTitle },
    ],
    showBackButton: true,
    hideSidebar: true,
    onBackPress: () => {
      router.replace(homePath as any);
    },
  };

  const unitDocumentTitle = buildUnitDisplayName({
    projectName: unit?.projectName ?? null,
    unitNumber: unit?.unitNumber ?? null,
  });
  const documentTitle =
    unit && unitDocumentTitle !== 'Unknown unit' ? unitDocumentTitle : labels.TITLE;
  useWebDocumentTitle(documentTitle);

  if (isLoading) {
    return (
      <WebAppLayout {...layoutProps}>
        <PropertiesDetailsScreenSkeleton
          isMobile={isCompact}
          carouselHeight={carouselHeight}
        />
      </WebAppLayout>
    );
  }

  if (unitNotFound || !unit) {
    return (
      <WebAppLayout {...layoutProps}>
        <View
          style={[
            styles.container,
            styles.notFoundContainer,
            { backgroundColor: colors.backgroundLevel2 },
          ]}
        >
          <Typography variant="title3" color="primary" style={{ textAlign: 'center', paddingHorizontal: 24 }}>
            {unitNotFound ? labels.TOAST_UNIT_UNAVAILABLE : labels.UNIT_LOAD_ERROR}
          </Typography>
        </View>
      </WebAppLayout>
    );
  }

  return (
    <PropertiesDetailsLoaded
      unit={unit}
      unitImages={unitImages}
      unitImagesLoading={unitImagesLoading}
      carouselHeight={carouselHeight}
      isMobile={isCompact}
      isPhone={isPhone}
      isTablet={isTablet}
      isRTL={isRTL}
      colors={colors}
      labels={labels}
      nextStepVisible={nextStepVisible}
      setNextStepVisible={setNextStepVisible}
      appointmentFlow={appointmentFlow}
      openBookAppointment={openBookAppointment}
      handleMapPress={handleMapPress}
      handleReserveUnit={onReserveUnitTracked}
      isReserveDisabled={isReserveDisabled}
      activeReservationErrorVisible={activeReservationErrorVisible}
      handleCloseActiveReservationError={handleCloseActiveReservationError}
      documentErrorMessage={documentErrorMessage}
      handleCloseDocumentError={handleCloseDocumentError}
      handleViewReservation={handleViewReservation}
      handleCloseReservationSummary={handleCloseReservationSummary}
      reservationSummaryVisible={reservationSummaryVisible}
      handleCloseConfirmReservation={handleCloseConfirmReservation}
      handleProceedReservation={handleProceedReservation}
      confirmReservationVisible={confirmReservationVisible}
      handleCloseUnitReservedSuccess={handleCloseUnitReservedSuccess}
      unitReservedSuccessVisible={unitReservedSuccessVisible}
      isUnitReserved={isUnitReserved}
      reservationSignatureStatus={reservationSignatureStatus}
      reservationMappedStatus={reservationMappedStatus}
      reservedDateDisplay={reservedDateDisplay}
      requestedOnDisplay={requestedOnDisplay}
      cancelReservationVisible={cancelReservationVisible}
      reservationCanceledVisible={reservationCanceledVisible}
      handleCloseCancelReservation={handleCloseCancelReservation}
      handleConfirmCancelReservation={handleConfirmCancelReservation}
      handleCloseReservationCanceled={handleCloseReservationCanceled}
      isFloorPlanAvailable={isFloorPlanAvailable}
      handleViewFloorPlan={handleViewFloorPlan}
      floorPlanLoading={floorPlanLoading}
      floorPlanSuccessMessage={floorPlanSuccessMessage}
      floorPlanSavedUri={floorPlanSavedUri}
      floorPlanErrorMessage={floorPlanErrorMessage}
      handleCloseFloorPlanSuccess={handleCloseFloorPlanSuccess}
      handleCloseFloorPlanError={handleCloseFloorPlanError}
      handleDocumentPress={onDocumentPressTracked}
      downloadingDocumentId={downloadingDocumentId}
      reservationRequestPending={reservationRequestPending}
      bookingDetailSyncPending={bookingDetailSyncPending}
      isAppointmentBookingEnabled={isAppointmentBookingEnabled}
      profileIskanLoanAmount={profileIskanLoanAmount}
      layoutProps={layoutProps}
      isFocused={isFocused}
      onGalleryOpen={onGalleryOpen}
      onUnitShare={onUnitShare}
    />
  );
}



type DetailsUnit = NonNullable<ReturnType<typeof usePropertiesDetails>['unit']>;
type DetailsColors = ReturnType<typeof useUIColors>;
type DetailsLabels = UnitDetailsLabels;
type DetailsFlow = ReturnType<typeof useBookAppointmentFlow>;
type DetailsReservationStatus = ReturnType<
  typeof usePropertiesDetails
>['reservationSignatureStatus'];

function resolveDetailsReservationCtas(
  unit: DetailsUnit,
  isUnitReserved: boolean,
  reservationSignatureStatus: DetailsReservationStatus,
  appointmentExists: boolean,
) {
  return getReservationCtas({
    appointmentExists,
    isLocallyReserved: isUnitReserved,
    localSignatureStatus:
      reservationSignatureStatus as LocalReservationSignatureStatus,
    ...(isUnitReserved && unit.reservationStatus
      ? { salesforceStatus: unit.reservationStatus }
      : {}),
  });
}

function DetailsGallery({
  unitImages,
  unitImagesLoading,
  carouselHeight,
  isMobile,
  isPhone,
  constructionCompletedLabel,
  isRTL,
  onGalleryOpen,
}: Readonly<{
  unitImages: ReturnType<typeof usePropertiesDetails>['unitImages'];
  unitImagesLoading: boolean;
  carouselHeight: number;
  isMobile: boolean;
  isPhone: boolean;
  constructionCompletedLabel: string | undefined;
  isRTL: boolean;
  onGalleryOpen: () => void;
}>) {
  return (
    <View style={styles.carouselWrap}>
      <ImageCarousel
        images={unitImages}
        height={carouselHeight}
        showNavigationButtons={!isPhone}
        onPress={onGalleryOpen}
        loading={unitImagesLoading}
      />
      {constructionCompletedLabel ? (
        <View
          style={[
            styles.constructionBadge,
            isMobile && styles.constructionBadgeMobile,
            isRTL && styles.constructionBadgeRTL,
            isMobile && isRTL && styles.constructionBadgeMobileRTL,
          ]}
          pointerEvents="none"
        >
          <Typography
            variant="caption"
            color="primary"
            style={[
              styles.constructionBadgeText,
              isMobile && styles.constructionBadgeTextMobile,
            ]}
          >
            {constructionCompletedLabel}
          </Typography>
        </View>
      ) : null}
    </View>
  );
}

function DetailsHeader({
  unitTitle,
  unit,
  isCompact,
  isPhone,
  isRTL,
  colors,
  labels,
  onMapPress,
}: Readonly<{
  unitTitle: string;
  unit: DetailsUnit;
  isCompact: boolean;
  isPhone: boolean;
  isRTL: boolean;
  colors: DetailsColors;
  labels: DetailsLabels;
  onMapPress: () => void;
}>) {
  const webRtlText =
    Platform.OS === 'web' && isRTL
      ? ({ textAlign: 'right' as const, writingDirection: 'rtl' as const })
      : null;

  return (
    <View style={[styles.unitHeader, isCompact && styles.unitHeaderMobile]}>
      <View
        style={[styles.titleRow, isRTL && styles.titleRowRTL]}
      >
        <Typography
          variant={isPhone ? 'title2' : 'title1'}
          color="primary"
          style={[styles.titleText, webRtlText]}
        >
          {unitTitle}
        </Typography>
        {(Platform.OS !== 'web' || (unit.latitude && unit.longitude)) ? (
          <Pressable
            onPress={onMapPress}
            accessibilityRole="button"
            accessibilityLabel={unitTitle}
          >
            <MapPinnedIcon size={24} color={colors.fontPrimary} />
          </Pressable>
        ) : null}
      </View>
      {hasTextValue(unit.description) ? (
        <Typography
          variant="bodyLarge"
          color="secondary"
          style={[
            styles.projectDescription,
            isPhone && styles.projectDescriptionMobile,
            webRtlText,
          ]}
        >
          {unit.description}
        </Typography>
      ) : null}
      {hasTextValue(unit.handoverDate) ? (
        <Typography
          variant="bodyLarge"
          color="secondary"
          style={[styles.handoverText, webRtlText]}
        >
          {labels.HANDOVER_PREFIX}{' '}
          {isRTL ? `\u200E${unit.handoverDate}` : unit.handoverDate}
        </Typography>
      ) : null}
    </View>
  );
}

function BookAppointmentSidebarCard({
  appointmentFlow,
  appointmentBookingEnabled,
  colors,
  labels,
  openBookAppointment,
}: Readonly<{
  appointmentFlow: DetailsFlow;
  appointmentBookingEnabled: boolean;
  colors: DetailsColors;
  labels: DetailsLabels;
  openBookAppointment: () => void;
}>) {
  if (appointmentFlow.hasAppointment && appointmentFlow.appointment) {
    return (
      <AppointmentStatusCard
        appointment={appointmentFlow.appointment}
        onCancel={appointmentFlow.openCancel}
        onReschedule={appointmentFlow.openReschedule}
        iconBackgroundColor={UI_PRIMITIVES.creamLighter}
        iconColor={colors.appointmentIconTint}
      />
    );
  }
  if (!appointmentBookingEnabled) {
    return null;
  }
  if (appointmentFlow.isUpcomingLoading) {
    return <UnitActionCardSkeleton />;
  }
  return (
    <UnitActionCard
      icon={<CalendarIcon size={24} color={colors.appointmentIconTint} />}
      iconBackgroundColor={UI_PRIMITIVES.creamLighter}
      title={labels.BOOK_APPOINTMENT}
      description={labels.BOOK_APPOINTMENT_DESCRIPTION}
      buttonTitle={labels.BOOK_APPOINTMENT_BUTTON}
      onPress={openBookAppointment}
    />
  );
}

function DetailsSidebar({
  unit,
  colors,
  labels,
  appointmentFlow,
  isUnitReserved,
  reservationSignatureStatus,
  reservationMappedStatus,
  reservedDateDisplay,
  openBookAppointment,
  handleReserveUnit,
  handlePayNow,
  isReserveDisabled,
  bookingDetailSyncPending,
  isAppointmentBookingEnabled,
}: Readonly<{
  unit: DetailsUnit;
  colors: DetailsColors;
  labels: DetailsLabels;
  appointmentFlow: DetailsFlow;
  isUnitReserved: boolean;
  reservationSignatureStatus: DetailsReservationStatus;
  reservationMappedStatus: ReservationStatus | null;
  reservedDateDisplay: string;
  openBookAppointment: () => void;
  handleReserveUnit: () => void;
  handlePayNow: () => void;
  isReserveDisabled: boolean;
  bookingDetailSyncPending: boolean;
  isAppointmentBookingEnabled: boolean;
}>) {
  const reservationCtas = resolveDetailsReservationCtas(
    unit,
    isUnitReserved,
    reservationSignatureStatus,
    appointmentFlow.hasAppointment,
  );
  const showViewReservation =
    reservationCtas.reservation === ReservationCta.VIEW_RESERVATION;
  const showReserveUnit = !showViewReservation && !isReserveDisabled;

  let reservationSlot: React.ReactNode = null;
  if (bookingDetailSyncPending) {
    reservationSlot = <UnitActionCardSkeleton />;
  } else if (showViewReservation) {
    reservationSlot = (
      <ReservationStatusCard
        reservedDateDisplay={reservedDateDisplay}
        status={reservationSignatureStatus}
        mappedStatus={reservationMappedStatus}
        {...(unit.reservationStatus
          ? { rawStatus: unit.reservationStatus }
          : {})}
        onCancel={handleReserveUnit}
        canCancelReservation={reservationCtas.canCancelReservation}
      />
    );
  } else if (showReserveUnit) {
    reservationSlot = (
      <UnitActionCard
        icon={<HouseIcon size={24} color={colors.reserveIconTint} />}
        iconBackgroundColor={UI_PRIMITIVES.greenLighter}
        title={labels.RESERVE_UNIT}
        description={labels.RESERVE_UNIT_DESCRIPTION}
        buttonTitle={labels.RESERVE_UNIT}
        onPress={handleReserveUnit}
      />
    );
  }

  return (
    <View
      style={[styles.sidebarCard, { backgroundColor: colors.backgroundLevel1 }]}
    >
      <UnitPriceSummary
        totalPrice={unit.totalPrice}
        preApprovalAmount={unit.preApprovalAmount ?? 0}
        onPayNow={handlePayNow}
        isUnitReserved={isUnitReserved}
      />
      <BookAppointmentSidebarCard
        appointmentFlow={appointmentFlow}
        appointmentBookingEnabled={isAppointmentBookingEnabled}
        colors={colors}
        labels={labels}
        openBookAppointment={openBookAppointment}
      />
      {reservationSlot}
    </View>
  );
}

function DetailsScrollContent({
  isMobile,
  isRTL,
  gallery,
  header,
  about,
  documents,
  mobilePrice,
  sidebar,
}: Readonly<{
  isMobile: boolean;
  isRTL: boolean;
  gallery: React.ReactNode;
  header: React.ReactNode;
  about: React.ReactNode;
  documents: React.ReactNode;
  mobilePrice: React.ReactNode;
  sidebar: React.ReactNode;
}>) {
  if (isMobile) {
    return (
      <>
        {gallery}
        {header}
        {mobilePrice}
        {about}
        {documents}
      </>
    );
  }
  return (
    <View style={styles.contentRow}>
      <View style={styles.leftColumn}>
        {gallery}
        {header}
        {about}
        {documents}
      </View>
      <View style={styles.rightColumn}>{sidebar}</View>
    </View>
  );
}

function DetailsStickyFooter({
  unit,
  isUnitReserved,
  isReserveDisabled,
  reservationSignatureStatus,
  reservationMappedStatus,
  reservationSummaryVisible,
  appointmentFlow,
  handleReserveUnit,
  handleViewReservation,
  openNextStep,
  openBookAppointment,
  bookingDetailSyncPending,
  isAppointmentBookingEnabled,
}: Readonly<{
  unit: DetailsUnit;
  isUnitReserved: boolean;
  isReserveDisabled: boolean;
  reservationSignatureStatus: DetailsReservationStatus;
  reservationMappedStatus: ReservationStatus | null;
  reservationSummaryVisible: boolean;
  appointmentFlow: DetailsFlow;
  handleReserveUnit: () => void;
  handleViewReservation: () => void;
  openNextStep: () => void;
  openBookAppointment: () => void;
  bookingDetailSyncPending: boolean;
  isAppointmentBookingEnabled: boolean;
}>) {
  const colors = useUIColors();
  const reservationCtas = resolveDetailsReservationCtas(
    unit,
    isUnitReserved,
    reservationSignatureStatus,
    appointmentFlow.hasAppointment,
  );
  const onBookPress =
    reservationCtas.appointment === ReservationCta.VIEW_APPOINTMENT
      ? openNextStep
      : openBookAppointment;
  const isReservedCta =
    reservationCtas.reservation === ReservationCta.VIEW_RESERVATION;
  const showReserveSlot = isReservedCta || !isReserveDisabled;
  const showBookSlot =
    reservationCtas.appointment === ReservationCta.VIEW_APPOINTMENT ||
    isAppointmentBookingEnabled;
  if (!showReserveSlot && !showBookSlot) {
    return null;
  }
  const reservedBannerStatus = unit.reservationStatus
    ? { rawStatus: unit.reservationStatus }
    : {};
  return (
    <View
      style={[
        styles.stickyFooterWrap,
        { backgroundColor: colors.backgroundLevel2 },
      ]}
    >
      {isReservedCta && !reservationSummaryVisible ? (
        <UnitReservedStickyBanner
          mappedStatus={reservationMappedStatus}
          {...reservedBannerStatus}
        />
      ) : null}
      <UnitStickyFooter
        reservationCta={reservationCtas.reservation}
        appointmentCta={reservationCtas.appointment}
        isAppointmentLoading={appointmentFlow.isUpcomingLoading}
        isReservationLoading={bookingDetailSyncPending}
        reserveDisabled={isReserveDisabled}
        appointmentBookingDisabled={!isAppointmentBookingEnabled}
        onReservePress={handleReserveUnit}
        onViewReservation={handleViewReservation}
        onBookPress={onBookPress}
      />
    </View>
  );
}

function DetailsFloorPlanDialogs({
  t,
  floorPlanErrorMessage,
  floorPlanSuccessMessage,
  floorPlanSavedUri,
  handleCloseFloorPlanError,
  handleCloseFloorPlanSuccess,
  onUnitShare,
}: Readonly<{
  t: ReturnType<typeof useTranslation>['t'];
  floorPlanErrorMessage: string | null;
  floorPlanSuccessMessage: string | null;
  floorPlanSavedUri: string | null;
  handleCloseFloorPlanError: () => void;
  handleCloseFloorPlanSuccess: () => void;
  onUnitShare: () => void;
}>) {
  const successOkText =
    Platform.OS === 'ios' ? t('common.saveShare') : t('common.ok');
  const handleSuccessOk = async () => {
    if (Platform.OS === 'ios' && floorPlanSavedUri) {
      onUnitShare?.();
      const shareResult = await shareFile({ uri: floorPlanSavedUri });
      if (!shareResult.success && shareResult.error) {
        logger.warn('PropertiesDetailsScreen', 'Sharing failed', {
          error: shareResult.error,
          fileUri: floorPlanSavedUri,
        });
      }
    }
    handleCloseFloorPlanSuccess();
  };
  return (
    <>
      <OkDialog
        visible={floorPlanErrorMessage !== null}
        message={floorPlanErrorMessage ?? ''}
        onOk={handleCloseFloorPlanError}
      />
      <OkDialog
        visible={floorPlanSuccessMessage !== null}
        title={t('common.downloadSuccessTitle')}
        message={floorPlanSuccessMessage ?? ''}
        okText={successOkText}
        onOk={handleSuccessOk}
      />
    </>
  );
}

function DetailsOverlays({
  t,
  unit,
  isFocused,
  appointmentFlow,
  nextStepVisible,
  closeNextStep,
  reservationSummaryVisible,
  handleCloseReservationSummary,
  handleReserveUnit,
  confirmReservationVisible,
  confirmDialogImage,
  handleCloseConfirmReservation,
  handleProceedReservation,
  unitReservedSuccessVisible,
  handleCloseUnitReservedSuccess,
  cancelReservationVisible,
  handleCloseCancelReservation,
  handleConfirmCancelReservation,
  reservationCanceledVisible,
  handleCloseReservationCanceled,
  reservationSignatureStatus,
  reservationMappedStatus,
  requestedOnDisplay,
  floorPlanErrorMessage,
  floorPlanSuccessMessage,
  floorPlanSavedUri,
  handleCloseFloorPlanError,
  handleCloseFloorPlanSuccess,
  onUnitShare,
  activeReservationErrorVisible,
  handleCloseActiveReservationError,
  documentErrorMessage,
  handleCloseDocumentError,
  activeReservationMessage,
  reservationRequestPending,
  profileIskanLoanAmount,
}: Readonly<{
  t: ReturnType<typeof useTranslation>['t'];
  unit: DetailsUnit;
  isFocused: boolean;
  appointmentFlow: DetailsFlow;
  nextStepVisible: boolean;
  closeNextStep: () => void;
  reservationSummaryVisible: boolean;
  handleCloseReservationSummary: () => void;
  handleReserveUnit: () => void;
  confirmReservationVisible: boolean;
  confirmDialogImage: ReturnType<
    typeof usePropertiesDetails
  >['unitImages'][number] | undefined;
  handleCloseConfirmReservation: () => void;
  handleProceedReservation: () => void;
  unitReservedSuccessVisible: boolean;
  handleCloseUnitReservedSuccess: () => void;
  cancelReservationVisible: boolean;
  handleCloseCancelReservation: () => void;
  handleConfirmCancelReservation: () => void;
  reservationCanceledVisible: boolean;
  handleCloseReservationCanceled: () => void;
  reservationSignatureStatus: DetailsReservationStatus;
  reservationMappedStatus: ReservationStatus | null;
  requestedOnDisplay: string;
  floorPlanErrorMessage: string | null;
  floorPlanSuccessMessage: string | null;
  floorPlanSavedUri: string | null;
  handleCloseFloorPlanError: () => void;
  handleCloseFloorPlanSuccess: () => void;
  onUnitShare: () => void;
  activeReservationErrorVisible: boolean;
  handleCloseActiveReservationError: () => void;
  documentErrorMessage: string | null;
  handleCloseDocumentError: () => void;
  activeReservationMessage: string;
  reservationRequestPending: boolean;
  profileIskanLoanAmount: number;
}>) {
  const upcomingAppointment = appointmentFlow.hasAppointment
    ? appointmentFlow.appointment
    : undefined;
  const reservationCtas = resolveDetailsReservationCtas(
    unit,
    true,
    reservationSignatureStatus,
    appointmentFlow.hasAppointment,
  );
  const summaryRawStatus = unit.reservationStatus
    ? { rawStatus: unit.reservationStatus }
    : {};
  return (
    <>
      {upcomingAppointment ? (
        <UnitNextStepSheet
          visible={nextStepVisible}
          onClose={closeNextStep}
          appointment={upcomingAppointment}
          onCancelAppointment={() => {
            appointmentFlow.openCancel();
          }}
          onRescheduleAppointment={() => {
            appointmentFlow.openReschedule();
          }}
        />
      ) : null}
      {isFocused ? <AppointmentFlowDialogs flow={appointmentFlow} /> : null}
      {isFocused ? (
        <ReservationSummarySheet
          visible={reservationSummaryVisible}
          onClose={handleCloseReservationSummary}
          totalPrice={unit.totalPrice}
          iskanLoanAmount={unit.preApprovalAmount ?? 0}
          requestedOnDisplay={requestedOnDisplay}
          status={reservationSignatureStatus}
          mappedStatus={reservationMappedStatus}
          {...summaryRawStatus}
          onCancelReservation={handleReserveUnit}
          canCancelReservation={reservationCtas.canCancelReservation}
        />
      ) : null}
      {isFocused ? (
        <ConfirmReservationDialog
          visible={confirmReservationVisible}
          unit={unit}
          iskanLoanAmount={profileIskanLoanAmount}
          imageSource={confirmDialogImage}
          onCancel={handleCloseConfirmReservation}
          onProceed={handleProceedReservation}
        />
      ) : null}
      {isFocused ? (
        <UnitReservedSuccessDialog
          visible={unitReservedSuccessVisible}
          onOk={handleCloseUnitReservedSuccess}
        />
      ) : null}
      {isFocused ? (
        <CancelReservationDialog
          visible={cancelReservationVisible}
          onNo={handleCloseCancelReservation}
          onYes={handleConfirmCancelReservation}
        />
      ) : null}
      {isFocused ? (
        <ReservationCanceledDialog
          visible={reservationCanceledVisible}
          onDismiss={handleCloseReservationCanceled}
        />
      ) : null}
      <DetailsFloorPlanDialogs
        t={t}
        floorPlanErrorMessage={floorPlanErrorMessage}
        floorPlanSuccessMessage={floorPlanSuccessMessage}
        floorPlanSavedUri={floorPlanSavedUri}
        handleCloseFloorPlanError={handleCloseFloorPlanError}
        handleCloseFloorPlanSuccess={handleCloseFloorPlanSuccess}
        onUnitShare={onUnitShare}
      />
      <OkDialog
        visible={activeReservationErrorVisible}
        message={activeReservationMessage}
        onOk={handleCloseActiveReservationError}
      />
      <MessageOkDialog
        visible={documentErrorMessage !== null}
        message={documentErrorMessage ?? ''}
        onOk={handleCloseDocumentError}
      />
      <FullScreenLoader visible={reservationRequestPending} />
    </>
  );
}

function PropertiesDetailsLoaded({
  unit,
  unitImages,
  unitImagesLoading,
  carouselHeight,
  isMobile,
  isPhone,
  isTablet,
  isRTL,
  colors,
  labels,
  nextStepVisible,
  setNextStepVisible,
  appointmentFlow,
  openBookAppointment,
  handleMapPress,
  handleReserveUnit,
  isReserveDisabled,
  activeReservationErrorVisible,
  handleCloseActiveReservationError,
  documentErrorMessage,
  handleCloseDocumentError,
  handleViewReservation,
  handleCloseReservationSummary,
  reservationSummaryVisible,
  handleCloseConfirmReservation,
  handleProceedReservation,
  confirmReservationVisible,
  handleCloseUnitReservedSuccess,
  unitReservedSuccessVisible,
  isUnitReserved,
  reservationSignatureStatus,
  reservationMappedStatus,
  reservedDateDisplay,
  requestedOnDisplay,
  cancelReservationVisible,
  reservationCanceledVisible,
  handleCloseCancelReservation,
  handleConfirmCancelReservation,
  handleCloseReservationCanceled,
  isFloorPlanAvailable,
  handleViewFloorPlan,
  floorPlanLoading,
  floorPlanSuccessMessage,
  floorPlanSavedUri,
  floorPlanErrorMessage,
  handleCloseFloorPlanSuccess,
  handleCloseFloorPlanError,
  handleDocumentPress,
  downloadingDocumentId,
  reservationRequestPending,
  bookingDetailSyncPending,
  isAppointmentBookingEnabled,
  profileIskanLoanAmount,
  layoutProps,
  isFocused,
  onGalleryOpen,
  onUnitShare,
}: Readonly<{
  unit: NonNullable<ReturnType<typeof usePropertiesDetails>['unit']>;
  unitImages: ReturnType<typeof usePropertiesDetails>['unitImages'];
  unitImagesLoading: boolean;
  carouselHeight: number;
  isMobile: boolean;
  isPhone: boolean;
  isTablet: boolean;
  isRTL: boolean;
  colors: ReturnType<typeof useUIColors>;
  labels: UnitDetailsLabels;
  nextStepVisible: boolean;
  setNextStepVisible: React.Dispatch<React.SetStateAction<boolean>>;
  appointmentFlow: ReturnType<typeof useBookAppointmentFlow>;
  openBookAppointment: () => void;
  handleMapPress: () => void;
  handleReserveUnit: () => void;
  isReserveDisabled: boolean;
  activeReservationErrorVisible: boolean;
  handleCloseActiveReservationError: () => void;
  documentErrorMessage: string | null;
  handleCloseDocumentError: () => void;
  handleViewReservation: () => void;
  handleCloseReservationSummary: () => void;
  reservationSummaryVisible: boolean;
  handleCloseConfirmReservation: () => void;
  handleProceedReservation: () => void;
  confirmReservationVisible: boolean;
  handleCloseUnitReservedSuccess: () => void;
  unitReservedSuccessVisible: boolean;
  isUnitReserved: boolean;
  reservationSignatureStatus: ReturnType<
    typeof usePropertiesDetails
  >['reservationSignatureStatus'];
  reservationMappedStatus: ReservationStatus | null;
  reservedDateDisplay: string;
  requestedOnDisplay: string;
  cancelReservationVisible: boolean;
  reservationCanceledVisible: boolean;
  handleCloseCancelReservation: () => void;
  handleConfirmCancelReservation: () => void;
  handleCloseReservationCanceled: () => void;
  isFloorPlanAvailable: boolean;
  handleViewFloorPlan: () => void;
  floorPlanLoading: boolean;
  floorPlanSuccessMessage: string | null;
  floorPlanSavedUri: string | null;
  floorPlanErrorMessage: string | null;
  handleCloseFloorPlanSuccess: () => void;
  handleCloseFloorPlanError: () => void;
  handleDocumentPress: (documentId: string) => void;
  downloadingDocumentId: string | null;
  reservationRequestPending: boolean;
  bookingDetailSyncPending: boolean;
  isAppointmentBookingEnabled: boolean;
  profileIskanLoanAmount: number;
  layoutProps: {
    activeTab: 'home';
    headerTitle: string;
    breadcrumbs: { label: string; path?: string }[];
    showBackButton: boolean;
    hideSidebar: boolean;
    onBackPress: () => void;
  };
  isFocused: boolean;
  onGalleryOpen: () => void;
  onUnitShare: () => void;
}>) {
  const { t } = useTranslation();
  const paymentFlow = useCompletePaymentFlow();
  const { open: handlePayNow, close: closePayNow } = paymentFlow;
  const unitTitle = [unit.unitNumber, unit.projectName, unit.projectLocation]
    .filter(Boolean)
    .join(', ');

  const openNextStep = () => setNextStepVisible(true);
  const closeNextStep = () => setNextStepVisible(false);
  const confirmDialogImage = unitImages[0];
  const isDesktop = !isMobile;
  const mobileReservationCtas = isMobile
    ? resolveDetailsReservationCtas(
        unit,
        isUnitReserved,
        reservationSignatureStatus,
        appointmentFlow.hasAppointment,
      )
    : null;
  const showMobileReservedBanner =
    mobileReservationCtas?.reservation === ReservationCta.VIEW_RESERVATION &&
    !reservationSummaryVisible;

  const iskanLoanAmount = unit.preApprovalAmount ?? 0;
  const outstandingBalance = Math.max(0, unit.totalPrice - iskanLoanAmount);

  React.useEffect(() => {
    if (!isFocused) {
      closePayNow();
    }
  }, [isFocused, closePayNow]);

  return (
    <WebAppLayout {...layoutProps}>
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
      >
        <View style={styles.scrollClip}>
          <ScrollView
            style={[
              styles.contentArea,
              isDesktop &&
                (isRTL ? styles.contentAreaRTL : styles.contentAreaLTR),
            ]}
            contentContainerStyle={[
              styles.contentScroll,
              isPhone && styles.contentScrollMobile,
              isTablet && styles.contentScrollTablet,
              isDesktop &&
                (isRTL
                  ? styles.contentScrollGutterRTL
                  : styles.contentScrollGutterLTR),
              isMobile && styles.contentScrollWithFooter,
              showMobileReservedBanner && styles.contentScrollWithReservedBanner,
            ]}
          >
          <DetailsScrollContent
            isMobile={isMobile}
            isRTL={isRTL}
            gallery={
              <DetailsGallery
                unitImages={unitImages}
                unitImagesLoading={unitImagesLoading}
                carouselHeight={carouselHeight}
                isMobile={isMobile}
                isPhone={isPhone}
                constructionCompletedLabel={undefined}
                isRTL={isRTL}
                onGalleryOpen={onGalleryOpen}
              />
            }
            header={
              <DetailsHeader
                unitTitle={unitTitle}
                unit={unit}
                isCompact={isMobile}
                isPhone={isPhone}
                isRTL={isRTL}
                colors={colors}
                labels={labels}
                onMapPress={handleMapPress}
              />
            }
            about={
              <UnitAboutAccordion
                bedrooms={unit.bedrooms}
                bathrooms={unit.bathrooms}
                areaSqFt={unit.areaSqFt}
                amenities={unit.amenities}
                onViewFloorPlan={handleViewFloorPlan}
                showFloorPlan={isFloorPlanAvailable}
                floorPlanLoading={floorPlanLoading}
              />
            }
            documents={
              <UnitDocumentsAccordion
                documents={unit.documents}
                onDocumentPress={handleDocumentPress}
                downloadingDocumentId={downloadingDocumentId}
              />
            }
            mobilePrice={
              <UnitPriceSummary
                standalone
                totalPrice={unit.totalPrice}
                preApprovalAmount={unit.preApprovalAmount ?? 0}
                onPayNow={handlePayNow}
                isUnitReserved={isUnitReserved}
              />
            }
            sidebar={
              <DetailsSidebar
                unit={unit}
                colors={colors}
                labels={labels}
                appointmentFlow={appointmentFlow}
                isUnitReserved={isUnitReserved}
                isReserveDisabled={isReserveDisabled}
                reservationSignatureStatus={reservationSignatureStatus}
                reservationMappedStatus={reservationMappedStatus}
                reservedDateDisplay={reservedDateDisplay}
                openBookAppointment={openBookAppointment}
                handleReserveUnit={handleReserveUnit}
                handlePayNow={handlePayNow}
                bookingDetailSyncPending={bookingDetailSyncPending}
                isAppointmentBookingEnabled={isAppointmentBookingEnabled}
              />
            }
          />
          </ScrollView>
        </View>

        {isMobile ? (
          <DetailsStickyFooter
            unit={unit}
            isUnitReserved={isUnitReserved}
            isReserveDisabled={isReserveDisabled}
            reservationSignatureStatus={reservationSignatureStatus}
            reservationMappedStatus={reservationMappedStatus}
            reservationSummaryVisible={reservationSummaryVisible}
            appointmentFlow={appointmentFlow}
            handleReserveUnit={handleReserveUnit}
            handleViewReservation={handleViewReservation}
            openNextStep={openNextStep}
            openBookAppointment={openBookAppointment}
            bookingDetailSyncPending={bookingDetailSyncPending}
            isAppointmentBookingEnabled={isAppointmentBookingEnabled}
          />
        ) : null}
      </View>

      <DetailsOverlays
        t={t}
        unit={unit}
        isFocused={isFocused}
        appointmentFlow={appointmentFlow}
        nextStepVisible={nextStepVisible}
        closeNextStep={closeNextStep}
        reservationSummaryVisible={reservationSummaryVisible}
        handleCloseReservationSummary={handleCloseReservationSummary}
        handleReserveUnit={handleReserveUnit}
        confirmReservationVisible={confirmReservationVisible}
        confirmDialogImage={confirmDialogImage}
        handleCloseConfirmReservation={handleCloseConfirmReservation}
        handleProceedReservation={handleProceedReservation}
        unitReservedSuccessVisible={unitReservedSuccessVisible}
        handleCloseUnitReservedSuccess={handleCloseUnitReservedSuccess}
        cancelReservationVisible={cancelReservationVisible}
        handleCloseCancelReservation={handleCloseCancelReservation}
        handleConfirmCancelReservation={handleConfirmCancelReservation}
        reservationCanceledVisible={reservationCanceledVisible}
        handleCloseReservationCanceled={handleCloseReservationCanceled}
        reservationSignatureStatus={reservationSignatureStatus}
        reservationMappedStatus={reservationMappedStatus}
        requestedOnDisplay={requestedOnDisplay}
        floorPlanErrorMessage={floorPlanErrorMessage}
        floorPlanSuccessMessage={floorPlanSuccessMessage}
        floorPlanSavedUri={floorPlanSavedUri}
        handleCloseFloorPlanError={handleCloseFloorPlanError}
        handleCloseFloorPlanSuccess={handleCloseFloorPlanSuccess}
        onUnitShare={onUnitShare}
        activeReservationErrorVisible={activeReservationErrorVisible}
        handleCloseActiveReservationError={handleCloseActiveReservationError}
        documentErrorMessage={documentErrorMessage}
        handleCloseDocumentError={handleCloseDocumentError}
        activeReservationMessage={labels.TOAST_ACTIVE_RESERVATION}
        reservationRequestPending={reservationRequestPending}
        profileIskanLoanAmount={profileIskanLoanAmount}
      />
      {isFocused ? (
        <CompletePaymentDialog
          visible={paymentFlow.isMethodVisible}
          onClose={closePayNow}
          onContinue={() => {
            void paymentFlow.continueFromMethod();
          }}
          totalPrice={unit.totalPrice}
          iskanLoanAmount={iskanLoanAmount}
          outstandingBalance={outstandingBalance}
          selectedMethod={paymentFlow.method}
          onSelectMethod={paymentFlow.setMethod}
          canContinue={paymentFlow.canContinue}
        />
      ) : null}
      {isFocused ? (
        <BankTransferDetailsDialog
          visible={paymentFlow.isBankTransferVisible}
          onBack={paymentFlow.backToMethod}
          onConfirmTransfer={() => {
            void paymentFlow.confirmTransfer();
          }}
          onClose={closePayNow}
          confirmDisabled={paymentFlow.isUpdating}
        />
      ) : null}
      {isFocused ? (
        <SubmissionReceivedDialog
          visible={paymentFlow.isSubmissionReceivedVisible}
          onBackToUnitDetails={closePayNow}
          method={paymentFlow.method}
        />
      ) : null}
      <OkDialog
        visible={paymentFlow.errorMessage !== null}
        message={paymentFlow.errorMessage ?? ''}
        onOk={paymentFlow.clearError}
      />
      <FullScreenLoader visible={paymentFlow.isUpdating} />
    </WebAppLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
    position: 'relative',
  },
  notFoundContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    padding: 24,
  },
  scrollClip: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minHeight: 0,
    overflow: 'hidden',
    zIndex: 0,
    position: 'relative',
  },
  contentArea: {
    flex: 1,
  },
  contentAreaLTR: {
    marginRight: -16,
  },
  contentAreaRTL: {
    marginLeft: -16,
  },
  contentScroll: {
    gap: 16,
    paddingHorizontal: 24,
    paddingTop: WEB_PAGE_TOP_BELOW_HEADER,
    paddingBottom: 24,
  },
  contentScrollMobile: {
    gap: 12,
    // Shell already has 16px side padding on mobile — don't stack another 24
    paddingHorizontal: 0,
  },
  contentScrollTablet: {
    gap: 16,
    paddingHorizontal: 8,
    paddingBottom: 16,
  },
  contentScrollGutterLTR: {
    paddingRight: 16,
  },
  contentScrollGutterRTL: {
    paddingLeft: 16,
  },
  contentScrollWithFooter: {
    paddingBottom: 24,
  },
  contentScrollWithReservedBanner: {
    paddingBottom: 32,
  },
  contentRow: {
    flexDirection: 'row',
    gap: 24,
    alignItems: 'flex-start',
  },
  // Design column widths (729px / 442px) used as flex ratios
  leftColumn: {
    flex: 729,
    gap: 22,
  },
  rightColumn: {
    flex: 442,
  },
  unitHeader: {
    gap: 8,
  },
  // 12px stack gap + 12px here = 24px between title block and price card
  unitHeaderMobile: {
    marginBottom: 12,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  titleRowRTL: {
    flexDirection: 'row-reverse',
  },
  titleText: {
    flex: 1,
  },
  projectDescription: {
    marginTop: 4,
  },
  /** Phone: 14px instead of the bodyLarge 18px. */
  projectDescriptionMobile: {
    fontSize: 14,
    lineHeight: 14 * 1.2,
  },
  handoverText: {
    marginTop: 0,
  },
  carouselWrap: {
    position: 'relative',
    width: '100%',
  },
  constructionBadge: {
    position: 'absolute',
    top: 16,
    left: 16,
    maxWidth: '85%',
    backgroundColor: '#E7E7E7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    zIndex: 3,
  },
  constructionBadgeRTL: {
    left: 'auto',
    right: 16,
  },
  constructionBadgeMobile: {
    top: 12,
    left: 12,
    paddingHorizontal: 6,
  },
  constructionBadgeMobileRTL: {
    left: 'auto',
    right: 12,
  },
  constructionBadgeText: {
    fontFamily: 'Matter-SemiBold',
    lineHeight: 16,
  },
  constructionBadgeTextMobile: {
    lineHeight: 22,
  },
  // Counteract WebAppLayout mobile shell side padding (16px) so the bar
  // sits flush with the viewport edges. Top padding + z-index keep the green
  // banner border from being painted over by scroll content (RN Web).
  stickyFooterWrap: {
    marginHorizontal: -16,
    marginBottom: 0,
    paddingTop: 12,
    flexShrink: 0,
    zIndex: 10,
    elevation: 10,
    position: 'relative',
    overflow: 'visible',
  },
  sidebarCard: {
    borderRadius: 12,
    paddingTop: 8,
    paddingHorizontal: 12,
    paddingBottom: 24,
    gap: 12,
  },
});

export default PropertiesDetailsScreen;
